/**
 * useThemeCustomization — T97P2
 *
 * Manages font family, accent color, text size, and border radius.
 * Applies CSS custom properties to document.documentElement in real-time
 * and auto-saves to backend via PATCH /auth/profile.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import { updateProfile } from '@/features/auth/authSlice';
import { useTheme } from '@/components/theme/ThemeProvider';

import {
  FONT_OPTIONS,
  BORDER_RADIUS_OPTIONS,
  DEFAULTS,
  isValidHex,
  ThemeCustomization,
  PersistedThemeCustomization,
  mapThemeCustomizationForPersistence,
  mapThemeCustomizationFromPersistence,
} from '@/lib/themeCustomizationMappers';
// ─── Constants ────────────────────────────────────────────────────────────────

export const ACCENT_PRESETS = [
  { name: 'Blue', hex: '#3b82f6' },
  { name: 'Teal', hex: '#14b8a6' },
  { name: 'Emerald', hex: '#10b981' },
  { name: 'Violet', hex: '#8b5cf6' },
  { name: 'Rose', hex: '#f43f5e' },
  { name: 'Amber', hex: '#f59e0b' },
  { name: 'Sky', hex: '#0ea5e9' },
  { name: 'Indigo', hex: '#6366f1' },
] as const;

export const TEXT_SIZE_OPTIONS = [
  { value: 'small', label: 'S', scale: 0.875 },
  { value: 'medium', label: 'M', scale: 1 },
  { value: 'large', label: 'L', scale: 1.125 },
] as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Convert hex color to HSL string (e.g. "199 89% 48%") */
export function hexToHSL(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '221.2 83.2% 53.3%'; // fallback to default primary

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** Compute a light foreground color based on the accent luminance */
function getForegroundHSL(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '210 40% 98%';
  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  // If the accent is light, use dark foreground; otherwise use light
  return luminance > 0.5 ? '222.2 47.4% 11.2%' : '210 40% 98%';
}

/**
 * background tint). Drops saturation ~70% and anchors lightness to the
 * surrounding sidebar background for theme-readable active-route contrast.
 */
function softerAccentHSL(hex: string, isDark: boolean): string {
  const fullHsl = hexToHSL(hex);
  const match = /^(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%$/.exec(fullHsl);
  if (!match) return fullHsl;
  const h = parseFloat(match[1]);
  const s = parseFloat(match[2]);
  const softenedS = Math.max(8, s * 0.3);
  const softenedL = isDark ? 24 : 92;
  return `${Math.round(h)} ${Math.round(softenedS)}% ${Math.round(softenedL)}%`;
}

// ─── Apply CSS variables ──────────────────────────────────────────────────────

function applyCSSVariables(config: ThemeCustomization) {
  const root = document.documentElement;
  const isDark = root.classList.contains('dark');

  // Font family
  const font = FONT_OPTIONS.find(f => f.value === config.fontFamily) ?? FONT_OPTIONS[0];
  root.style.setProperty('--font-family', font.css);

  // Text scale
  const size = TEXT_SIZE_OPTIONS.find(s => s.value === config.textSize) ?? TEXT_SIZE_OPTIONS[1];
  root.style.setProperty('--text-scale', String(size.scale));

  // Border radius
  const radius = BORDER_RADIUS_OPTIONS.find(r => r.value === config.borderRadius) ?? BORDER_RADIUS_OPTIONS[1];
  root.style.setProperty('--radius', radius.css);

  // Accent color → override --primary, --ring, and sidebar visual identity
  if (isValidHex(config.accentColor)) {
    const hsl = hexToHSL(config.accentColor);
    const fg = getForegroundHSL(config.accentColor);
    root.style.setProperty('--primary', hsl);
    root.style.setProperty('--primary-foreground', fg);
    root.style.setProperty('--ring', hsl);
    // [T277-PY] Bind the user's accent to the sidebar: 3px left-edge rail
    // (--sidebar-primary), foreground text on that rail, and the active-route
    // background tint (--sidebar-accent), softened for the active theme.
    root.style.setProperty('--sidebar-primary', hsl);
    root.style.setProperty('--sidebar-primary-foreground', fg);
    root.style.setProperty('--sidebar-accent', softerAccentHSL(config.accentColor, isDark));
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useThemeCustomization() {
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.auth.user);
  const { theme } = useTheme();

  // Parse user preferences into theme config
  const getInitialConfig = useCallback((): ThemeCustomization => {
    const prefs = user?.preferences?.theme;
    if (!prefs || typeof prefs !== 'object') {
      // theme might be a simple string like "light" — use defaults
      if (typeof prefs === 'string') return DEFAULTS;
      return DEFAULTS;
    }
    // prefs may be a legacy UI-domain object or the new canonical backend shape.
    // Always run it through the read-boundary mapper so canonical `sm|md|lg`
    // becomes `small|medium|large` for the Settings UI + CSS application.
    const p = prefs as unknown as Partial<ThemeCustomization> | PersistedThemeCustomization;
    const normalized = mapThemeCustomizationFromPersistence(p as PersistedThemeCustomization);
    const legacy = p as Partial<ThemeCustomization>;
    return {
      fontFamily: legacy.fontFamily ?? normalized.fontFamily,
      accentColor: normalized.accentColor,
      textSize: normalized.textSize,
      borderRadius: normalized.borderRadius,
    };
  }, [user?.preferences?.theme]);

  const [config, setConfig] = useState<ThemeCustomization>(getInitialConfig);
  const saveTimer = useRef<ReturnType<typeof setTimeout>>();

  // Apply on mount and when config changes
  useEffect(() => {
    applyCSSVariables(config);
  }, [config]);

  // Re-apply theme-sensitive sidebar variables when ThemeProvider toggles <html>.dark.
  useEffect(() => {
    const observer = new MutationObserver(() => applyCSSVariables(config));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [config]);

  // Sync from user profile when it loads
  useEffect(() => {
    setConfig(getInitialConfig());
  }, [getInitialConfig]);

  // Auto-save with debounce
  const persistToBackend = useCallback((newConfig: ThemeCustomization) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      dispatch(updateProfile({
        preferences: {
          theme: mapThemeCustomizationForPersistence(newConfig, theme) as unknown as string,
        },
      }));
    }, 600);
  }, [dispatch, theme]);

  const updateConfig = useCallback((partial: Partial<ThemeCustomization>) => {
    setConfig(prev => {
      const next = { ...prev, ...partial };
      applyCSSVariables(next);
      persistToBackend(next);
      return next;
    });
  }, [persistToBackend]);

  return {
    config,
    updateConfig,
    FONT_OPTIONS,
    ACCENT_PRESETS,
    TEXT_SIZE_OPTIONS,
    BORDER_RADIUS_OPTIONS,
  };
}

export {
  FONT_OPTIONS,
  BORDER_RADIUS_OPTIONS,
  type ThemeCustomization,
  type PersistedThemeCustomization,
  mapThemeCustomizationForPersistence,
  mapThemeCustomizationFromPersistence,
} from '@/lib/themeCustomizationMappers';
